import React, { useEffect, useRef, useState, useCallback } from "react";

/* ============================================================================
   ARCHER FOUNDATION UGANDA — Homepage
   Premium Single-File React Component
   Light Blue Theme · Gallery Caption Fix · Mobile Gallery Improvements
   ============================================================================ */

// Logo now served from CDN (no local import needed)
const LOGO_URL = "https://acher-gallery.sfo3.cdn.digitaloceanspaces.com/logo.jpg";

const MEDIA = {
  heroVideo:
    "https://cdn.coverr.co/videos/coverr-doctor-checking-a-babys-heartbeat-2632/1080p.mp4",
  heroPoster:
    "https://images.unsplash.com/photo-1584515933487-779824d29309?auto=format&fit=crop&w=1800&q=80",
  aboutImage:
    "https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=1200&q=80",
  whyExistBg:
    "https://images.unsplash.com/photo-1518005020951-eccb494ad742?auto=format&fit=crop&w=1800&q=80",
  supportBg:
    "https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1800&q=80",
  storyVideo: "https://www.youtube.com/embed/dQw4w9WgXcQ",

  // Logo (CDN)
  logo: LOGO_URL,
};

// Gallery images from CDN
const GALLERY_IMAGES = [
  "mat-10.jpg",
  "mat11.jpg",
  "mat91.png",
  "mat93.png",
  "mat94.png",
  "mat95.png",
  "nurse1.jpg",
  "nurse2.jpg",
  "nurse3.jpg",
  "sec1.jpg",
  "sec2.jpg",
  "sec3.jpg",
  "sec4.jpg",
  "team1.jpg",
  "team2.jpg",
  "team3.jpg",
];

const GALLERY_CDN_BASE = "https://acher-gallery.sfo3.cdn.digitaloceanspaces.com/";

// Gallery metadata for enhanced display
const GALLERY_META = {
  "mat-10.jpg": { 
    title: "Community Health Outreach", 
    location: "Kampala, Uganda",
    category: "outreach",
    date: "2024",
    description: "Mobile health clinic serving remote communities"
  },
  "mat11.jpg": { 
    title: "HIV Testing & Counseling", 
    location: "Jinja, Uganda",
    category: "healthcare",
    date: "2024",
    description: "Confidential HIV testing and prevention education"
  },
  "mat91.png": { 
    title: "Archer Team 2024", 
    location: "Kampala, Uganda",
    category: "team",
    date: "2024",
    description: "Our dedicated healthcare professionals"
  },
  "mat93.png": { 
    title: "Team Meeting", 
    location: "Kampala, Uganda",
    category: "team",
    date: "2024",
    description: "Strategic planning for community health"
  },
  "mat94.png": { 
    title: "Healthcare Team", 
    location: "Kampala, Uganda",
    category: "team",
    date: "2024",
    description: "Committed to serving vulnerable communities"
  },
  "mat95.png": { 
    title: "Volunteer Training", 
    location: "Kampala, Uganda",
    category: "team",
    date: "2024",
    description: "Empowering community health volunteers"
  },
  "nurse1.jpg": { 
    title: "Maternal Care", 
    location: "Kampala, Uganda",
    category: "healthcare",
    date: "2024",
    description: "Supporting mothers and newborns"
  },
  "nurse2.jpg": { 
    title: "Child Health Check", 
    location: "Kampala, Uganda",
    category: "healthcare",
    date: "2024",
    description: "Immunization and child wellness programs"
  },
  "nurse3.jpg": { 
    title: "Pediatric Care", 
    location: "Kampala, Uganda",
    category: "healthcare",
    date: "2024",
    description: "Ensuring every child gets quality healthcare"
  },
  "sec1.jpg": { 
    title: "Community Event", 
    location: "Kampala, Uganda",
    category: "events",
    date: "2024",
    description: "Bringing healthcare closer to communities"
  },
  "sec2.jpg": { 
    title: "Health Awareness", 
    location: "Jinja, Uganda",
    category: "events",
    date: "2024",
    description: "Educating communities on health and wellbeing"
  },
  "sec3.jpg": { 
    title: "Outreach Program", 
    location: "Gulu, Uganda",
    category: "outreach",
    date: "2024",
    description: "Reaching underserved communities"
  },
  "sec4.jpg": { 
    title: "Medical Camp", 
    location: "Mbale, Uganda",
    category: "outreach",
    date: "2024",
    description: "Free medical services for vulnerable families"
  },
  "team1.jpg": { 
    title: "Team Building", 
    location: "Kampala, Uganda",
    category: "team",
    date: "2024",
    description: "Strong teams deliver better healthcare"
  },
  "team2.jpg": { 
    title: "Field Team", 
    location: "Kampala, Uganda",
    category: "team",
    date: "2024",
    description: "On-the-ground healthcare delivery"
  },
  "team3.jpg": { 
    title: "Medical Staff", 
    location: "Kampala, Uganda",
    category: "team",
    date: "2024",
    description: "Our skilled healthcare workforce"
  },
};

const CATEGORIES = [
  { id: "all", label: "All Photos", icon: "🖼️" },
  { id: "outreach", label: "Outreach", icon: "🏥" },
  { id: "healthcare", label: "Healthcare", icon: "💉" },
  { id: "team", label: "Our Team", icon: "👥" },
  { id: "events", label: "Events", icon: "📅" },
];

const IMPACT_STATS = [
  { value: 10000, suffix: "+", label: "People Tested for HIV" },
  { value: 5000, suffix: "+", label: "Women Supported with SRH" },
  { value: 2500, suffix: "+", label: "Children Vaccinated" },
  { value: 100, suffix: "+", label: "Community Outreach Camps Held" },
];

const BARRIERS = [
  { icon: "🏚️", title: "Poverty", text: "Many families cannot afford the cost of travel or treatment." },
  { icon: "🛣️", title: "Distance", text: "Health facilities are often hours away from rural communities." },
  { icon: "🚫", title: "Stigma", text: "Fear of judgement keeps many from seeking the care they need." },
  { icon: "📖", title: "Limited Information", text: "Many communities lack accurate, accessible health education." },
];

const PROGRAMS = [
  {
    icon: "🎗️",
    title: "HIV Prevention and Care",
    summary: "Early testing, prevention services, treatment support, and education so people can live healthy, fulfilling lives.",
    more: "Our services include HIV counselling and testing, linkage to HIV care, ART/ARV treatment support, treatment adherence counselling, prevention education, referral for viral load monitoring, and community awareness campaigns. Our goal is to reduce new infections and ensure people living with HIV get the support they need.",
  },
  {
    icon: "♀️",
    title: "SRH for Women in Sex Work",
    summary: "Confidential, respectful, non-discriminatory healthcare that protects health, restores dignity, and empowers women.",
    more: "Services include HIV counselling and testing, PrEP for HIV prevention, linkage to ART services, condom education and distribution, family planning, STI screening and referral, sexual health education, psychosocial support, violence response and referral, and community outreach. When vulnerable women receive care without fear or discrimination, whole communities grow stronger.",
  },
  {
    icon: "🤱",
    title: "Maternal & Child Health",
    summary: "Healthy mothers, healthy children, stronger communities — every step of the way.",
    more: "We support antenatal care education, postnatal support, immunisation awareness, nutrition education, breastfeeding support, child growth monitoring, and family planning counselling — because every mother deserves a safe pregnancy and every child deserves the chance to grow healthy.",
  },
  {
    icon: "✚",
    title: "Community Outreach",
    summary: "Taking essential healthcare directly to the people who need it most.",
    more: "Our outreach camps offer free medical care including HIV testing, malaria testing, blood pressure and diabetes screening, family planning services, immunisation, health education, and medical referrals — reaching communities that cannot easily get to a facility.",
  },
];

const SUPPORT_ITEMS = [
  { icon: "🩸", label: "HIV test kits" },
  { icon: "🛡️", label: "Condoms & prevention supplies" },
  { icon: "💊", label: "Family planning commodities" },
  { icon: "📚", label: "Health education materials" },
  { icon: "🚑", label: "Community medical outreach" },
  { icon: "🤲", label: "Support for vulnerable populations" },
];

const PARTNERS = ["Forever Medicare Life Sure", "TIKO", "Uganda Private Midwives Association", "Maisha Maids"];

// Foundation Objectives - All 25
const ALL_OBJECTIVES = [
  "Health Promotion and Healthcare Access — Improving access to quality, affordable, and equitable healthcare services for vulnerable communities through medical outreach, health education, and strategic partnerships.",
  "Maternal, Newborn, and Child Health — Promoting maternal and child health through healthcare interventions, nutrition support, immunization advocacy, and reproductive health education.",
  "Education and Lifelong Learning — Supporting access to quality education through scholarships, school support, infrastructure development, and literacy initiatives.",
  "Youth Empowerment and Leadership Development — Empowering young people through leadership development, vocational training, mentorship, and entrepreneurship support.",
  "Poverty Reduction and Economic Empowerment — Contributing to poverty eradication through sustainable livelihoods, income-generating activities, and financial literacy programs.",
  "Food Security and Nutrition — Combating hunger and malnutrition through agricultural support, nutrition education, and food assistance initiatives.",
  "Water, Sanitation, and Hygiene (WASH) — Improving access to safe water, sanitation facilities, and hygiene education for community health and dignity.",
  "Child Protection and Welfare — Protecting and promoting the rights, welfare, and holistic development of vulnerable children.",
  "Family Strengthening and Social Protection — Strengthening families through social, economic, and psychosocial support programs.",
  "Women's Empowerment and Gender Equality — Promoting gender equality and empowering women and girls through education, skills development, and economic initiatives.",
  "Mental Health and Psychosocial Support — Advancing mental health awareness and providing psychosocial support for emotional wellbeing and resilience.",
  "Care and Support for Older Persons — Enhancing the quality of life, dignity, and wellbeing of older persons through healthcare and social support.",
  "Environmental Conservation and Climate Action — Promoting environmental conservation, climate resilience, and sustainable natural resource management.",
  "Culture, Heritage, and Creative Arts Promotion — Preserving and promoting cultural heritage, indigenous knowledge, and creative expression.",
  "Digital Inclusion and Technology Innovation — Leveraging digital technologies to improve education, healthcare, and entrepreneurship.",
  "Human Rights and Social Justice — Advocating for the protection of human rights, social justice, and equal opportunities for all.",
  "Community Development and Capacity Building — Strengthening communities through training, skills development, and citizen participation.",
  "Disaster Risk Reduction and Humanitarian Assistance — Providing emergency relief, disaster preparedness, and humanitarian support during crises.",
  "Research, Innovation, and Knowledge Sharing — Conducting and supporting research, data collection, and policy analysis for sustainable development.",
  "Public Health Awareness and Disease Prevention — Promoting public health awareness and contributing to disease prevention through education and advocacy.",
  "Peacebuilding, Unity, and Social Cohesion — Promoting peace, reconciliation, unity, and harmonious coexistence in communities.",
  "Faith, Ethics, and Moral Development — Promoting positive moral values, ethical leadership, and character development.",
  "Strategic Partnerships and Resource Mobilization — Establishing and strengthening partnerships to maximize development impact.",
  "Volunteerism and Civic Engagement — Encouraging volunteerism, community service, and civic responsibility.",
  "Sustainable Development and Institutional Growth — Undertaking activities that contribute to sustainable development and organizational growth."
];

// Display 6 objectives initially
const DISPLAYED_OBJECTIVES = ALL_OBJECTIVES.slice(0, 6);

const NAV_ITEMS = [
  { id: "about", label: "About" },
  { id: "programs", label: "Programs" },
  { id: "objectives", label: "Objectives" },
  { id: "support", label: "Support" },
  { id: "donate", label: "Donate" },
];

// LIGHT BLUE THEME COLORS
const COLORS = {
  primary: "#0A3B5C",      // deep light blue
  primaryLight: "#2C6B8F",
  primaryLighter: "#4A8BB5",
  primaryBg: "#E8F4F8",    // light blue background
  accent: "#E8A838",       // gold accent
  accentLight: "#f3c169",
  surface: "#FFFFFF",
  text: "#0A2A3F",
  textLight: "#4A6A7F",
  border: "rgba(10,59,92,0.08)",
  shadow: "rgba(10,59,92,0.06)",
};

// Global Styles with light blue theme
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&family=Open+Sans:wght@300;400;600;700&display=swap');
    
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    html {
      scroll-behavior: smooth;
    }
    
    body {
      font-family: 'Open Sans', -apple-system, BlinkMacSystemFont, sans-serif;
      color: ${COLORS.text};
      background: ${COLORS.primaryBg};
      overflow-x: hidden;
    }
    
    h1, h2, h3, h4 {
      font-family: 'Poppins', sans-serif;
      color: ${COLORS.primary};
      margin: 0 0 0.5em;
      line-height: 1.2;
    }
    
    p {
      line-height: 1.7;
      margin: 0 0 1em;
    }
    
    a {
      text-decoration: none;
      color: inherit;
    }
    
    ul {
      list-style: none;
      margin: 0;
      padding: 0;
    }
    
    img {
      max-width: 100%;
      display: block;
    }
    
    button {
      font-family: inherit;
      cursor: pointer;
    }

    /* ===== Enhanced Animations ===== */
    
    [data-reveal] {
      opacity: 0;
      transform: translateY(50px) scale(0.96);
      transition: opacity 0.9s cubic-bezier(0.22, 1, 0.36, 1), 
                  transform 0.9s cubic-bezier(0.22, 1, 0.36, 1);
    }
    
    [data-reveal].is-visible {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
    
    [data-reveal="left"] {
      opacity: 0;
      transform: translateX(-60px) scale(0.95);
      transition: opacity 0.9s cubic-bezier(0.22, 1, 0.36, 1), 
                  transform 0.9s cubic-bezier(0.22, 1, 0.36, 1);
    }
    
    [data-reveal="left"].is-visible {
      opacity: 1;
      transform: translateX(0) scale(1);
    }
    
    [data-reveal="right"] {
      opacity: 0;
      transform: translateX(60px) scale(0.95);
      transition: opacity 0.9s cubic-bezier(0.22, 1, 0.36, 1), 
                  transform 0.9s cubic-bezier(0.22, 1, 0.36, 1);
    }
    
    [data-reveal="right"].is-visible {
      opacity: 1;
      transform: translateX(0) scale(1);
    }
    
    [data-reveal="up"] {
      opacity: 0;
      transform: translateY(70px);
      transition: opacity 1s cubic-bezier(0.22, 1, 0.36, 1), 
                  transform 1s cubic-bezier(0.22, 1, 0.36, 1);
    }
    
    [data-reveal="up"].is-visible {
      opacity: 1;
      transform: translateY(0);
    }
    
    [data-reveal="scale"] {
      opacity: 0;
      transform: scale(0.85);
      transition: opacity 1s cubic-bezier(0.22, 1, 0.36, 1), 
                  transform 1s cubic-bezier(0.22, 1, 0.36, 1);
    }
    
    [data-reveal="scale"].is-visible {
      opacity: 1;
      transform: scale(1);
    }

    .stagger-children > * {
      opacity: 0;
      transform: translateY(30px);
      transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1), 
                  transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
    }
    
    .stagger-children.is-visible > *:nth-child(1) { transition-delay: 0.05s; }
    .stagger-children.is-visible > *:nth-child(2) { transition-delay: 0.12s; }
    .stagger-children.is-visible > *:nth-child(3) { transition-delay: 0.19s; }
    .stagger-children.is-visible > *:nth-child(4) { transition-delay: 0.26s; }
    .stagger-children.is-visible > *:nth-child(5) { transition-delay: 0.33s; }
    .stagger-children.is-visible > *:nth-child(6) { transition-delay: 0.40s; }
    .stagger-children.is-visible > *:nth-child(7) { transition-delay: 0.47s; }
    .stagger-children.is-visible > *:nth-child(8) { transition-delay: 0.54s; }
    .stagger-children.is-visible > * {
      opacity: 1;
      transform: translateY(0);
    }

    @media (prefers-reduced-motion: reduce) {
      [data-reveal], [data-reveal="left"], [data-reveal="right"], 
      [data-reveal="up"], [data-reveal="scale"], .stagger-children > * {
        opacity: 1 !important;
        transform: none !important;
        transition: none !important;
      }
    }

    @keyframes afuWordIn {
      0% { opacity: 0; transform: translateY(30px) rotateX(-10deg); }
      60% { opacity: 1; transform: translateY(-4px) rotateX(2deg); }
      100% { opacity: 1; transform: translateY(0) rotateX(0); }
    }
    
    @keyframes afuPulseBtn {
      0%, 100% { box-shadow: 0 8px 24px rgba(232,168,56,0.35); }
      50% { box-shadow: 0 8px 40px rgba(232,168,56,0.7), 0 0 60px rgba(232,168,56,0.2); }
    }
    
    @keyframes afuBounce {
      0%, 100% { transform: translateY(0) rotate(45deg); }
      50% { transform: translateY(12px) rotate(45deg); }
    }
    
    @keyframes afuKenBurns {
      0% { transform: scale(1) translate(0, 0); }
      50% { transform: scale(1.08) translate(-10px, -5px); }
      100% { transform: scale(1.12) translate(5px, 10px); }
    }
    
    @keyframes afuFadeIn {
      from { opacity: 0; transform: scale(0.92); }
      to { opacity: 1; transform: scale(1); }
    }
    
    @keyframes afuFloat {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-12px); }
    }

    @keyframes afuShimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }

    @keyframes afuSiteFadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes afuIntroTextIn {
      0% { opacity: 0; transform: translateY(14px); letter-spacing: 0.4em; }
      100% { opacity: 1; transform: translateY(0); letter-spacing: 0.2em; }
    }

    @keyframes afuWriteArcher {
      0% { clip-path: inset(0 100% 0 0); opacity: 0; }
      10% { opacity: 1; }
      50% { clip-path: inset(0 0% 0 0); opacity: 1; }
      90% { clip-path: inset(0 0% 0 0); opacity: 1; }
      100% { clip-path: inset(0 0% 0 0); opacity: 0; }
    }

    @keyframes galleryCardFloat {
      0%, 100% { transform: translateY(0) scale(1); }
      50% { transform: translateY(-8px) scale(1.02); }
    }

    /* ===== Intro logo cinematic sequence ===== */
    @keyframes afuLogoMaterialize {
      0% { opacity: 0; transform: scale(0.6) rotate(-6deg); filter: blur(18px) brightness(1.8); }
      55% { opacity: 1; transform: scale(1.08) rotate(1deg); filter: blur(0px) brightness(1.15); }
      100% { opacity: 1; transform: scale(1) rotate(0deg); filter: blur(0px) brightness(1); }
    }

    @keyframes afuLogoGlowPulse {
      0%, 100% { box-shadow: 0 0 40px 6px rgba(232,168,56,0.25), 0 0 120px 40px rgba(232,168,56,0.08); }
      50% { box-shadow: 0 0 70px 14px rgba(232,168,56,0.5), 0 0 160px 60px rgba(232,168,56,0.16); }
    }

    @keyframes afuLogoShine {
      0% { transform: translateX(-150%) skewX(-20deg); opacity: 0; }
      45% { opacity: 0.9; }
      100% { transform: translateX(150%) skewX(-20deg); opacity: 0; }
    }

    @keyframes afuRingExpand {
      0% { transform: scale(0.4); opacity: 0; border-width: 3px; }
      40% { opacity: 0.6; }
      100% { transform: scale(2.6); opacity: 0; border-width: 1px; }
    }

    .afu-logo-badge-spin {
      animation: afuFloat 5s ease-in-out infinite;
    }

    .afu-mini-logo {
      border-radius: 10px;
      object-fit: contain;
      background: #FFFFFF;
      box-shadow: 0 6px 20px rgba(10,59,92,0.18);
    }

    /* Mobile menu overlay */
    .mobile-overlay {
      position: fixed;
      inset: 0;
      background: rgba(5,20,35,0.32);
      z-index: 998;
      opacity: 0;
      visibility: hidden;
      transition: opacity 0.45s ease, visibility 0.45s ease;
      backdrop-filter: blur(6px);
      -webkit-backdrop-filter: blur(6px);
    }

    .mobile-overlay.is-open {
      opacity: 1;
      visibility: visible;
    }

    /* About Image Animation */
    .about-static-image {
      animation: afuKenBurns 20s ease-in-out infinite alternate, afuFloat 6s ease-in-out infinite;
      border-radius: 16px;
      box-shadow: 0 20px 60px rgba(10,59,92,0.15);
    }

    /* Gallery image load animation */
    @keyframes galleryImageLoad {
      0% { opacity: 0; transform: scale(0.95); }
      100% { opacity: 1; transform: scale(1); }
    }

    @keyframes gallerySlideIn {
      0% { opacity: 0; transform: scale(0.92) translateY(10px); }
      100% { opacity: 1; transform: scale(1) translateY(0); }
    }
  `}</style>
);

// ===== Hooks =====

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll("[data-reveal], [data-reveal='left'], [data-reveal='right'], [data-reveal='up'], [data-reveal='scale'], .stagger-children");
    if (!("IntersectionObserver" in window) || els.length === 0) {
      els.forEach((el) => el.classList.add("is-visible"));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  });
}

function useScrollProgressAndNav() {
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);
  const [activeSection, setActiveSection] = useState("top");

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollTop = doc.scrollTop || document.body.scrollTop;
      const scrollHeight = (doc.scrollHeight || document.body.scrollHeight) - doc.clientHeight;
      setScrolled(scrollTop > 60);
      setProgress(scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0);

      const sections = ["top", "about", "programs", "objectives", "support", "donate"];
      let current = "top";
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 150) {
            current = id;
          }
        }
      }
      setActiveSection(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return { scrolled, progress, activeSection };
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false
  );
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const handler = (e) => setReduced(e.matches);
    mq.addEventListener ? mq.addEventListener("change", handler) : mq.addListener(handler);
    return () =>
      mq.removeEventListener ? mq.removeEventListener("change", handler) : mq.removeListener(handler);
  }, []);
  return reduced;
}

// Preload gallery images in background
function preloadGalleryImages(images) {
  if (typeof window === 'undefined') return;
  
  images.forEach((imgName) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = GALLERY_CDN_BASE + imgName;
    
    if ('caches' in window) {
      try {
        caches.open('archer-gallery').then(cache => {
          const url = GALLERY_CDN_BASE + imgName;
          fetch(url).then(response => {
            if (response.ok) {
              cache.put(url, response);
            }
          }).catch(() => {});
        }).catch(() => {});
      } catch (e) {}
    }
  });
}

// Animated CountUp with light blue theme
function CountUp({ end, suffix = "", duration = 1800 }) {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const started = useRef(false);
  const reducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (reducedMotion) {
      setValue(end);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started.current) {
            started.current = true;
            const start = performance.now();
            const tick = (now) => {
              const progress = Math.min((now - start) / duration, 1);
              const eased = 1 - Math.pow(1 - progress, 3);
              setValue(Math.floor(eased * end));
              if (progress < 1) requestAnimationFrame(tick);
              else setValue(end);
            };
            requestAnimationFrame(tick);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [end, duration, reducedMotion]);

  return (
    <span ref={ref} style={{ 
      display: "block",
      fontFamily: "'Poppins', sans-serif",
      fontWeight: 800,
      fontSize: "clamp(32px, 3.5vw, 44px)",
      color: COLORS.primary,
      background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryLight} 100%)`,
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text"
    }}>
      {value.toLocaleString()}
      {suffix}
    </span>
  );
}

/* ============================================================================
   INTRO OVERLAY — logo materializes with a cinematic glow, holds,
   then shatters into pieces
   ============================================================================ */

const INTRO_GRID_COLS = 9;
const INTRO_GRID_ROWS = 6;
const INTRO_IN_MS = 650;
const INTRO_HOLD_MS = 1300;
const INTRO_SHATTER_MS = 1100;

function IntroOverlay({ onFinish }) {
  const [phase, setPhase] = useState("in");
  const reducedMotion = usePrefersReducedMotion();
  const finishedRef = useRef(false);

  const pieces = useRef(
    Array.from({ length: INTRO_GRID_COLS * INTRO_GRID_ROWS }, () => ({
      dx: (Math.random() - 0.5) * 1600,
      dy: (Math.random() - 0.5) * 1100,
      rot: (Math.random() - 0.5) * 640,
      delay: Math.random() * 0.32,
    }))
  ).current;

  const finish = useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    onFinish();
  }, [onFinish]);

  useEffect(() => {
    document.body.style.overflow = "hidden";

    if (reducedMotion) {
      const t = setTimeout(finish, 300);
      return () => {
        clearTimeout(t);
        document.body.style.overflow = "";
      };
    }

    const inTimer = setTimeout(() => setPhase("hold"), INTRO_IN_MS);
    const holdTimer = setTimeout(() => setPhase("shatter"), INTRO_IN_MS + INTRO_HOLD_MS);
    const doneTimer = setTimeout(() => {
      setPhase("done");
      finish();
    }, INTRO_IN_MS + INTRO_HOLD_MS + INTRO_SHATTER_MS);

    return () => {
      clearTimeout(inTimer);
      clearTimeout(holdTimer);
      clearTimeout(doneTimer);
      document.body.style.overflow = "";
    };
  }, [reducedMotion, finish]);

  if (reducedMotion || phase === "done") return null;

  const materializing = phase === "in";
  const shattering = phase === "shatter";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 20000,
        background: COLORS.primary,
        overflow: "hidden",
      }}
      aria-hidden="true"
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "radial-gradient(ellipse at 50% 50%, rgba(232,168,56,0.14) 0%, transparent 65%)",
          opacity: shattering ? 0 : 1,
          transition: "opacity 0.6s ease",
        }}
      />

      {/* Solid logo — materializes then dissolves into the shattered grid */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: shattering ? 0 : 1,
          transition: "opacity 0.35s ease",
          pointerEvents: "none",
        }}
      >
        <div style={{ position: "relative" }}>
          {/* expanding rings */}
          {!materializing && (
            <>
              <span style={{
                position: "absolute",
                inset: "-10px",
                borderRadius: "24px",
                border: "2px solid rgba(232,168,56,0.5)",
                animation: "afuRingExpand 2.6s ease-out infinite",
              }} />
              <span style={{
                position: "absolute",
                inset: "-10px",
                borderRadius: "24px",
                border: "2px solid rgba(232,168,56,0.5)",
                animation: "afuRingExpand 2.6s ease-out 0.9s infinite",
              }} />
            </>
          )}
          <div style={{
            position: "relative",
            width: "clamp(120px, 18vw, 190px)",
            height: "clamp(120px, 18vw, 190px)",
            borderRadius: "24px",
            overflow: "hidden",
            background: "#FFFFFF",
            animation: materializing
              ? `afuLogoMaterialize ${INTRO_IN_MS}ms cubic-bezier(0.22,1,0.36,1) forwards, afuLogoGlowPulse 2.4s ease-in-out ${INTRO_IN_MS}ms infinite`
              : `afuLogoGlowPulse 2.4s ease-in-out infinite`,
          }}>
            <img
              src={LOGO_URL}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
            {/* cinematic light sweep */}
            <div style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "60%",
              height: "100%",
              background: "linear-gradient(100deg, transparent, rgba(255,255,255,0.85), transparent)",
              animation: `afuLogoShine 2.2s ease-in-out ${INTRO_IN_MS + 200}ms 1`,
            }} />
          </div>
        </div>
      </div>

      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "grid",
          gridTemplateColumns: `repeat(${INTRO_GRID_COLS}, 1fr)`,
          gridTemplateRows: `repeat(${INTRO_GRID_ROWS}, 1fr)`,
        }}
      >
        {pieces.map((p, i) => {
          const col = i % INTRO_GRID_COLS;
          const row = Math.floor(i / INTRO_GRID_COLS);
          return (
            <div
              key={i}
              style={{
                backgroundImage: `url(${LOGO_URL})`,
                backgroundSize: `${INTRO_GRID_COLS * 100}% ${INTRO_GRID_ROWS * 100}%`,
                backgroundPosition: `${(col / (INTRO_GRID_COLS - 1)) * 100}% ${(row / (INTRO_GRID_ROWS - 1)) * 100}%`,
                backgroundRepeat: "no-repeat",
                willChange: "transform, opacity",
                transform: shattering
                  ? `translate(${p.dx}px, ${p.dy}px) rotate(${p.rot}deg) scale(0.35)`
                  : "translate(0, 0) rotate(0deg) scale(1)",
                opacity: shattering ? 0 : 0,
                transition: `transform ${INTRO_SHATTER_MS}ms cubic-bezier(0.55,0,0.85,0.15) ${p.delay}s, opacity ${INTRO_SHATTER_MS * 0.8}ms ease ${p.delay + 0.15}s`,
              }}
            />
          );
        })}
      </div>

      <div
        style={{
          position: "absolute",
          bottom: "12%",
          left: 0,
          right: 0,
          textAlign: "center",
          opacity: shattering ? 0 : 1,
          transition: "opacity 0.4s ease",
          pointerEvents: "none",
        }}
      >
        <p
          style={{
            display: "inline-block",
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 600,
            fontSize: "clamp(11px, 1.4vw, 14px)",
            color: "rgba(255,255,255,0.75)",
            textTransform: "uppercase",
            letterSpacing: "0.2em",
            animation: "afuIntroTextIn 0.9s cubic-bezier(0.22,1,0.36,1) 0.5s both",
          }}
        >
          Archer Foundation Uganda
        </p>
      </div>
    </div>
  );
}

// ===== ENHANCED GALLERY COMPONENT with fixed caption overlay =====

function EnhancedGalleryModal({ open, onClose }) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState({});
  const [touchStart, setTouchStart] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const [viewMode, setViewMode] = useState("grid");
  const containerRef = useRef(null);
  const categoriesContainerRef = useRef(null);

  // Get filtered images based on category
  const getFilteredImages = useCallback(() => {
    if (activeCategory === "all") {
      return GALLERY_IMAGES;
    }
    return GALLERY_IMAGES.filter(img => {
      const meta = GALLERY_META[img];
      return meta && meta.category === activeCategory;
    });
  }, [activeCategory]);

  const filteredImages = getFilteredImages();
  const currentImage = filteredImages[selectedIndex];
  const currentMeta = currentImage ? GALLERY_META[currentImage] : null;

  // Preload images
  useEffect(() => {
    if (!open) return;
    filteredImages.forEach((imgName, index) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = GALLERY_CDN_BASE + imgName;
      img.onload = () => {
        setLoadedImages(prev => ({ ...prev, [imgName]: true }));
      };
      img.onerror = () => {
        setLoadedImages(prev => ({ ...prev, [imgName]: true }));
      };
    });
  }, [open, filteredImages]);

  // Reset selected index when category changes
  useEffect(() => {
    setSelectedIndex(0);
    // Scroll categories container to show active category
    if (categoriesContainerRef.current) {
      const activeBtn = categoriesContainerRef.current.querySelector(`[data-category="${activeCategory}"]`);
      if (activeBtn) {
        activeBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [activeCategory]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && filteredImages.length > 0) {
        setSelectedIndex((prev) => (prev + 1) % filteredImages.length);
      }
      if (e.key === 'ArrowLeft' && filteredImages.length > 0) {
        setSelectedIndex((prev) => (prev - 1 + filteredImages.length) % filteredImages.length);
      }
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose, filteredImages.length]);

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (!touchStart || filteredImages.length === 0) return;
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        setSelectedIndex((prev) => (prev + 1) % filteredImages.length);
      } else {
        setSelectedIndex((prev) => (prev - 1 + filteredImages.length) % filteredImages.length);
      }
    }
    setTouchStart(null);
  };

  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    setViewMode("grid");
  };

  // When an image is clicked, switch to carousel view and set the selected index
  const handleImageClick = (index) => {
    setSelectedIndex(index);
    setViewMode("carousel");
  };

  // Render grid view with fixed caption (overlay on hover)
  const renderGridView = () => (
    <div className="afu-gallery-grid" style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
      gridAutoRows: "min-content",
      alignContent: "start",
      gap: "16px",
      padding: "20px",
      flex: "1",
      minHeight: 0,
      overflowY: "auto",
    }}>
      {filteredImages.map((imgName, index) => {
        const meta = GALLERY_META[imgName];
        const isLoaded = loadedImages[imgName];
        return (
          <div
            key={index}
            onClick={() => handleImageClick(index)}
            style={{
              position: "relative",
              aspectRatio: "1",
              borderRadius: "14px",
              overflow: "hidden",
              cursor: "pointer",
              background: COLORS.primaryLight,
              transition: "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.3s ease",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              animation: isLoaded ? "galleryImageLoad 0.5s cubic-bezier(0.22, 1, 0.36, 1) forwards" : "none",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.05)";
              e.currentTarget.style.boxShadow = "0 12px 40px rgba(10,59,92,0.25)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
            }}
          >
            <img
              src={GALLERY_CDN_BASE + imgName}
              alt={meta?.title || `Gallery ${index + 1}`}
              loading="lazy"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transition: "transform 0.5s ease",
              }}
            />
            {/* Caption overlay - appears on hover */}
            {meta && (
              <div style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                padding: "14px 16px",
                background: "linear-gradient(to top, rgba(10,59,92,0.85), transparent)",
                color: "#FFFFFF",
                fontSize: "13px",
                fontWeight: 500,
                opacity: 0,
                transition: "opacity 0.3s ease",
                pointerEvents: "none",
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
              onMouseLeave={(e) => e.currentTarget.style.opacity = "0"}>
                <div style={{ fontWeight: 600, textShadow: "0 1px 8px rgba(0,0,0,0.3)" }}>{meta.title}</div>
                <div style={{ fontSize: "11px", opacity: 0.85, textShadow: "0 1px 6px rgba(0,0,0,0.3)" }}>{meta.location}</div>
              </div>
            )}
            {!isLoaded && (
              <div style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "rgba(255,255,255,0.4)",
                fontSize: "12px",
              }}>
                Loading...
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // Render carousel view with caption at bottom
  const renderCarouselView = () => {
    if (filteredImages.length === 0) return null;
    const imgName = filteredImages[selectedIndex];
    const meta = GALLERY_META[imgName];
    const isLoaded = loadedImages[imgName];

    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        position: "relative",
      }}>
        <div style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px",
          position: "relative",
          minHeight: "300px",
        }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedIndex((prev) => (prev - 1 + filteredImages.length) % filteredImages.length);
            }}
            style={{
              position: "absolute",
              left: "8px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "rgba(255,255,255,0.15)",
              border: "none",
              color: "#FFFFFF",
              fontSize: "32px",
              width: "50px",
              height: "50px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              zIndex: 2,
              transition: "background 0.3s ease",
              backdropFilter: "blur(4px)",
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.25)"}
            onMouseLeave={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}
          >
            ‹
          </button>

          <img
            src={GALLERY_CDN_BASE + imgName}
            alt={meta?.title || `Gallery ${selectedIndex + 1}`}
            loading="lazy"
            style={{
              maxWidth: "100%",
              maxHeight: "70vh",
              minHeight: "300px",
              objectFit: "contain",
              borderRadius: "12px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
              opacity: isLoaded ? 1 : 0.3,
              transition: "opacity 0.4s ease, transform 0.4s ease",
              transform: isLoaded ? "scale(1)" : "scale(0.95)",
              animation: "gallerySlideIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) forwards",
              width: "auto",
              height: "auto",
            }}
          />

          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedIndex((prev) => (prev + 1) % filteredImages.length);
            }}
            style={{
              position: "absolute",
              right: "8px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "rgba(255,255,255,0.15)",
              border: "none",
              color: "#FFFFFF",
              fontSize: "32px",
              width: "50px",
              height: "50px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              zIndex: 2,
              transition: "background 0.3s ease",
              backdropFilter: "blur(4px)",
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.25)"}
            onMouseLeave={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}
          >
            ›
          </button>
        </div>

        {/* Caption bar - below image */}
        {meta && (
          <div style={{
            padding: "14px 24px",
            background: "rgba(10,59,92,0.85)",
            backdropFilter: "blur(8px)",
            color: "#FFFFFF",
            textAlign: "center",
            borderTop: "1px solid rgba(255,255,255,0.08)",
            flexShrink: 0,
          }}>
            <h3 style={{ color: "#FFFFFF", fontSize: "18px", marginBottom: "2px" }}>{meta.title}</h3>
            <p style={{ fontSize: "13px", opacity: 0.8, margin: 0 }}>{meta.location} · {meta.date}</p>
            <p style={{ fontSize: "13px", opacity: 0.7, margin: "4px 0 0" }}>{meta.description}</p>
          </div>
        )}

        {/* Thumbnails */}
        <div className="afu-gallery-thumbs" style={{
          padding: "10px 16px",
          display: "flex",
          gap: "8px",
          overflowX: "auto",
          overflowY: "hidden",
          background: "rgba(10,59,92,0.2)",
          flexShrink: 0,
          flexWrap: "nowrap",
          scrollBehavior: "smooth",
          WebkitOverflowScrolling: "touch",
        }}>
          {filteredImages.map((img, idx) => (
            <button
              key={idx}
              onClick={() => setSelectedIndex(idx)}
              style={{
                flexShrink: 0,
                width: "68px",
                height: "68px",
                border: idx === selectedIndex ? "3px solid #E8A838" : "2px solid rgba(255,255,255,0.1)",
                borderRadius: "8px",
                overflow: "hidden",
                background: "rgba(255,255,255,0.05)",
                padding: 0,
                transition: "border-color 0.3s ease, transform 0.2s ease",
                transform: idx === selectedIndex ? "scale(1.08)" : "scale(1)",
              }}
            >
              <img
                src={GALLERY_CDN_BASE + img}
                alt={`Thumbnail ${idx + 1}`}
                loading="lazy"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </button>
          ))}
        </div>
      </div>
    );
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(10,30,50,0.96)",
        zIndex: 10001,
        display: "flex",
        flexDirection: "column",
        animation: "afuFadeIn 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
        touchAction: "none",
      }}
      onClick={onClose}
    >
      {/* Always-visible close button (X) - only close button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label="Close gallery"
        style={{
          position: "absolute",
          top: "14px",
          right: "16px",
          zIndex: 20,
          width: "42px",
          height: "42px",
          borderRadius: "50%",
          background: "rgba(10,30,50,0.9)",
          border: "2px solid rgba(255,255,255,0.25)",
          color: "#FFFFFF",
          fontSize: "18px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          backdropFilter: "blur(8px)",
          boxShadow: "0 6px 20px rgba(0,0,0,0.35)",
          transition: "background 0.3s ease, transform 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "rgba(232,168,56,0.9)";
          e.currentTarget.style.transform = "scale(1.06)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "rgba(10,30,50,0.9)";
          e.currentTarget.style.transform = "scale(1)";
        }}
      >
        ✕
      </button>

      {/* Header - no "Back to Grid" button */}
      <div
        className="afu-gallery-header"
        style={{
          padding: "12px 64px 12px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "rgba(10,59,92,0.8)",
          backdropFilter: "blur(12px)",
          flexShrink: 0,
          zIndex: 2,
          borderBottom: "1px solid rgba(255,255,255,0.05)",
          flexWrap: "wrap",
          gap: "8px",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          flexShrink: 0,
        }}>
          <img src={LOGO_URL} alt="" className="afu-mini-logo" style={{ height: "26px", width: "26px" }} />
          <span style={{
            color: "rgba(255,255,255,0.6)",
            fontSize: "13px",
            fontFamily: "'Poppins', sans-serif",
          }}>
            {filteredImages.length > 0 ? `${selectedIndex + 1} / ${filteredImages.length}` : "0 images"}
          </span>
          <span style={{
            color: "rgba(255,255,255,0.3)",
            fontSize: "18px",
          }}>|</span>
          <span style={{
            color: "rgba(255,255,255,0.5)",
            fontSize: "13px",
            fontFamily: "'Poppins', sans-serif",
          }}>
            {activeCategory === "all" ? "All Photos" : CATEGORIES.find(c => c.id === activeCategory)?.label || "All"}
          </span>
        </div>

        {/* Category filters - horizontally scrollable on mobile */}
        <div 
          ref={categoriesContainerRef}
          className="afu-gallery-categories" 
          style={{
            display: "flex",
            gap: "6px",
            flexWrap: "nowrap",
            overflowX: "auto",
            overflowY: "hidden",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            maxWidth: "100%",
            padding: "2px 0 4px",
            flex: "1 1 auto",
            minWidth: 0,
            justifyContent: "flex-start",
          }}
        >
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              data-category={cat.id}
              onClick={(e) => {
                e.stopPropagation();
                handleCategoryChange(cat.id);
              }}
              style={{
                background: activeCategory === cat.id ? "rgba(232,168,56,0.2)" : "rgba(255,255,255,0.06)",
                border: activeCategory === cat.id ? "1px solid #E8A838" : "1px solid rgba(255,255,255,0.08)",
                color: activeCategory === cat.id ? "#E8A838" : "rgba(255,255,255,0.6)",
                padding: "6px 14px",
                borderRadius: "999px",
                fontSize: "12px",
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 500,
                transition: "all 0.3s ease",
                cursor: "pointer",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {cat.icon} {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div
        ref={containerRef}
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          position: "relative",
          touchAction: "pan-y",
          overflow: "hidden",
        }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onClick={(e) => e.stopPropagation()}
      >
        {viewMode === "grid" && renderGridView()}
        {viewMode === "carousel" && renderCarouselView()}
      </div>
    </div>
  );
}

function GalleryNavButton({ onOpenGallery, closeMobileMenu, mobile = false }) {
  return (
    <a
      href="#gallery"
      className="afu-gallery-trigger"
      onClick={(e) => {
        e.preventDefault();
        if (closeMobileMenu) closeMobileMenu();
        onOpenGallery();
      }}
      style={{
        color: "#FFFFFF",
        fontWeight: 500,
        fontSize: "14px",
        opacity: 0.85,
        fontFamily: "inherit",
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        borderBottom: "2px solid transparent",
        paddingBottom: "4px",
        letterSpacing: "0.3px",
        transition: "opacity 0.3s ease, color 0.3s ease, border-bottom-color 0.3s ease",
        cursor: "pointer",
      }}
    >
      <span style={{ fontSize: mobile ? "18px" : "16px" }}>🖼️</span>
      Gallery
    </a>
  );
}

function Navbar({ scrolled, menuOpen, setMenuOpen, onOpenGallery, activeSection }) {
  const isActive = (id) => activeSection === id;
  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      <div 
        className={`mobile-overlay ${menuOpen ? "is-open" : ""}`}
        onClick={() => setMenuOpen(false)}
      />
      
      <header 
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 999,
          background: scrolled ? "rgba(10,59,92,0.97)" : "rgba(10,59,92,0.95)",
          boxShadow: scrolled ? "0 8px 32px rgba(0,0,0,0.25)" : "0 4px 20px rgba(0,0,0,0.12)",
          transition: "background 0.4s ease, box-shadow 0.4s ease, padding 0.4s ease",
          padding: scrolled ? "10px 0" : "16px 0",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)"
        }}
        className={`afu-navbar ${menuOpen ? "is-open" : ""}`}
      >
        <div 
          style={{
            maxWidth: "1180px",
            margin: "0 auto",
            padding: "0 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px"
          }}
        >
          <a href="#top" style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: "12px", 
            fontFamily: "'Poppins', sans-serif", 
            color: "#FFFFFF", 
            fontSize: "16px",
            fontWeight: 600,
            flexShrink: 0
          }}>
            <img 
              src={LOGO_URL} 
              alt="Archer Foundation Uganda Logo" 
              className="afu-mini-logo"
              style={{ 
                height: "42px", 
                width: "42px", 
              }}
            />
            <span className="afu-brand-text" style={{ fontSize: "clamp(13px, 1.4vw, 17px)" }}>
              Archer <strong>Foundation</strong> Uganda
            </span>
          </a>

          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <nav 
              style={{
                display: "flex",
                alignItems: "center",
                gap: "26px"
              }}
              className={`afu-nav-links ${menuOpen ? "is-open" : ""}`}
            >
              {NAV_ITEMS.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={() => setMenuOpen(false)}
                  style={{
                    color: isActive(item.id) ? "#E8A838" : "#FFFFFF",
                    fontWeight: isActive(item.id) ? 600 : 500,
                    fontSize: "14px",
                    opacity: isActive(item.id) ? 1 : 0.85,
                    transition: "opacity 0.3s ease, color 0.3s ease, transform 0.3s ease",
                    borderBottom: isActive(item.id) ? "2px solid #E8A838" : "2px solid transparent",
                    paddingBottom: "4px",
                    letterSpacing: "0.3px"
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive(item.id)) {
                      e.currentTarget.style.color = "#E8A838";
                      e.currentTarget.style.opacity = "1";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive(item.id)) {
                      e.currentTarget.style.color = "#FFFFFF";
                      e.currentTarget.style.opacity = "0.85";
                    }
                  }}
                >
                  {item.label}
                </a>
              ))}

              {/* Gallery button - only visible in mobile dropdown */}
              <GalleryNavButton 
                onOpenGallery={onOpenGallery} 
                closeMobileMenu={closeMenu} 
                mobile={true}
              />
            </nav>

            {/* Desktop Donate button - only shown on desktop */}
            <a
              href="#donate"
              className="afu-nav-donate-btn"
              onClick={() => setMenuOpen(false)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 22px",
                borderRadius: "999px",
                fontWeight: 600,
                fontFamily: "'Poppins', sans-serif",
                fontSize: "13px",
                border: "2px solid transparent",
                whiteSpace: "nowrap",
                background: "linear-gradient(135deg, #E8A838, #d49a2e)",
                color: "#0A2A3F",
                boxShadow: "0 8px 24px rgba(232,168,56,0.35)",
                flexShrink: 0,
              }}
            >
              ❤️ Donate Now
            </a>

            <button
              className={`afu-burger ${menuOpen ? "is-open" : ""}`}
              aria-label="Toggle navigation menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)}
              style={{
                display: "none",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "5px",
                width: "42px",
                height: "42px",
                borderRadius: "12px",
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.15)",
                padding: 0,
                zIndex: 1001,
                transition: "background 0.3s ease, border-color 0.3s ease"
              }}
            >
              <span style={{ 
                width: "20px", 
                height: "2px", 
                background: "#FFFFFF", 
                borderRadius: "2px", 
                transition: "transform 0.3s ease, opacity 0.3s ease" 
              }} />
              <span style={{ 
                width: "20px", 
                height: "2px", 
                background: "#FFFFFF", 
                borderRadius: "2px", 
                transition: "transform 0.3s ease, opacity 0.3s ease" 
              }} />
              <span style={{ 
                width: "20px", 
                height: "2px", 
                background: "#FFFFFF", 
                borderRadius: "2px", 
                transition: "transform 0.3s ease, opacity 0.3s ease" 
              }} />
            </button>
          </div>
        </div>
      </header>
    </>
  );
}

// Static Animated Image Component
function StaticAnimatedImage({ src, alt, className = "" }) {
  return (
    <div style={{ height: "100%", minHeight: "400px", position: "relative", overflow: "hidden", borderRadius: "16px" }}>
      <img
        src={src}
        alt={alt}
        className={`about-static-image ${className}`}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          animation: "afuKenBurns 20s ease-in-out infinite alternate, afuFloat 6s ease-in-out infinite",
          borderRadius: "16px",
          boxShadow: "0 20px 60px rgba(10,59,92,0.15)",
        }}
      />
      <div style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: "40%",
        background: `linear-gradient(to top, ${COLORS.primary}40, transparent)`,
        zIndex: 2,
        pointerEvents: "none"
      }} />
    </div>
  );
}

function Hero({ onPlayVideo }) {
  return (
    <section id="top" style={{
      position: "relative",
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden"
    }}>
      <div style={{ position: "absolute", inset: 0, zIndex: 0 }}>
        <video
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          src={MEDIA.heroVideo}
          poster={MEDIA.heroPoster}
          autoPlay
          muted
          loop
          playsInline
        />
        <div style={{
          position: "absolute",
          inset: 0,
          background: `linear-gradient(180deg, ${COLORS.primary}DD 0%, ${COLORS.primaryLight}88 40%, ${COLORS.primary}DD 100%)`
        }} />
        <div style={{
          position: "absolute",
          inset: 0,
          background: "radial-gradient(ellipse at 50% 80%, rgba(232,168,56,0.08) 0%, transparent 70%)",
        }} />
      </div>

      <div style={{
        position: "relative",
        zIndex: 1,
        maxWidth: "900px",
        margin: "0 auto",
        padding: "140px 24px 80px",
        width: "100%",
        textAlign: "center"
      }}>
        <div className="afu-logo-badge-spin" style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px",
        }} data-reveal="scale">
          <img
            src={LOGO_URL}
            alt="Archer Foundation Uganda"
            className="afu-mini-logo"
            style={{ height: "64px", width: "64px", border: "3px solid rgba(232,168,56,0.6)" }}
          />
        </div>
        <p style={{
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 600,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          fontSize: "clamp(11px, 1.1vw, 14px)",
          color: "#E8A838",
          marginBottom: "16px"
        }} data-reveal>
          Kampala, Uganda · Community Healthcare
        </p>
        <h1 style={{
          color: "#FFFFFF",
          fontSize: "clamp(34px, 5.8vw, 68px)",
          fontWeight: 800,
          maxWidth: "900px",
          margin: "0 auto 24px",
          lineHeight: 1.1,
          textShadow: "0 4px 30px rgba(0,0,0,0.3)"
        }} data-reveal="up">
          {"Transforming Lives Through Healthcare, Hope, and Community Empowerment"
            .split(" ")
            .map((word, i) => (
              <span key={i} style={{
                display: "inline-block",
                opacity: 0,
                transform: "translateY(30px) rotateX(-10deg)",
                animation: `afuWordIn 0.8s cubic-bezier(0.22, 1, 0.36, 1) ${0.4 + i * 0.06}s forwards`
              }}>
                {word}&nbsp;
              </span>
            ))}
        </h1>
        <p style={{
          color: "rgba(255,255,255,0.92)",
          fontSize: "clamp(17px, 2vw, 21px)",
          maxWidth: "620px",
          margin: "0 auto 32px",
          fontWeight: 300
        }} data-reveal>
          Bringing lifesaving healthcare services to vulnerable communities across Uganda.
        </p>

        <div style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "16px",
          justifyContent: "center",
          marginBottom: "32px"
        }} data-reveal>
          <a href="#donate" className="afu-pulse-cta" style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "16px 36px",
            borderRadius: "999px",
            fontWeight: 600,
            fontFamily: "'Poppins', sans-serif",
            fontSize: "15px",
            border: "2px solid transparent",
            transition: "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), background 0.3s ease, box-shadow 0.3s ease",
            whiteSpace: "nowrap",
            background: "linear-gradient(135deg, #E8A838, #d49a2e)",
            color: "#0A2A3F",
            boxShadow: "0 8px 30px rgba(232,168,56,0.4)",
            animation: "afuPulseBtn 2.4s ease-in-out 1.4s 2"
          }}>
            ❤️ Donate Now
          </a>
          <a href="#contact" style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "16px 32px",
            borderRadius: "999px",
            fontWeight: 600,
            fontFamily: "'Poppins', sans-serif",
            fontSize: "15px",
            border: "2px solid rgba(255,255,255,0.5)",
            transition: "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), background 0.3s ease",
            whiteSpace: "nowrap",
            background: "rgba(255,255,255,0.06)",
            color: "#FFFFFF",
            backdropFilter: "blur(8px)"
          }}>
            🤝 Become a Partner
          </a>
          <a href="#contact" style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "16px 32px",
            borderRadius: "999px",
            fontWeight: 600,
            fontFamily: "'Poppins', sans-serif",
            fontSize: "15px",
            border: "2px solid rgba(255,255,255,0.5)",
            transition: "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), background 0.3s ease",
            whiteSpace: "nowrap",
            background: "rgba(255,255,255,0.06)",
            color: "#FFFFFF",
            backdropFilter: "blur(8px)"
          }}>
            👥 Volunteer with Us
          </a>
        </div>

        <button onClick={onPlayVideo} style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "10px",
          background: "none",
          border: "none",
          color: "#FFFFFF",
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 500,
          fontSize: "15px",
          opacity: 0.85,
          cursor: "pointer",
          transition: "opacity 0.3s ease, transform 0.3s ease"
        }}>
          <span style={{
            width: "38px",
            height: "38px",
            borderRadius: "50%",
            border: "2px solid rgba(255,255,255,0.6)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "13px",
            transition: "background 0.3s ease"
          }}>▶</span> Watch our story
        </button>
      </div>

      <a href="#about" aria-label="Scroll down" style={{
        position: "absolute",
        bottom: "32px",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1
      }}>
        <span style={{
          display: "block",
          width: "24px",
          height: "24px",
          borderRight: "2px solid rgba(255,255,255,0.7)",
          borderBottom: "2px solid rgba(255,255,255,0.7)",
          transform: "rotate(45deg)",
          animation: "afuBounce 1.8s ease-in-out infinite",
          opacity: 0.7
        }} />
      </a>
    </section>
  );
}

function ImpactSnapshot() {
  return (
    <section id="impact" style={{
      padding: "60px 0 80px",
      position: "relative",
      background: "#FFFFFF"
    }}>
      <div style={{
        maxWidth: "1180px",
        margin: "0 auto",
        padding: "0 24px"
      }}>
        <div className="stagger-children" style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "24px"
        }}>
          {IMPACT_STATS.map((stat) => (
            <div key={stat.label} style={{
              background: `linear-gradient(135deg, ${COLORS.primaryBg}, #FFFFFF)`,
              borderRadius: "20px",
              padding: "36px 20px",
              textAlign: "center",
              border: `1px solid ${COLORS.border}`,
              boxShadow: `0 4px 20px ${COLORS.shadow}`,
              transition: "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.4s ease"
            }}>
              <CountUp end={stat.value} suffix={stat.suffix} />
              <p style={{
                color: COLORS.textLight,
                fontSize: "14.5px",
                margin: "8px 0 0",
                fontWeight: 500
              }}>{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function About() {
  const aboutImageUrl = "https://acher-gallery.sfo3.cdn.digitaloceanspaces.com/sec2.jpg";

  return (
    <section id="about" style={{
      padding: "100px 0",
      position: "relative",
      background: COLORS.primaryBg
    }}>
      <div style={{
        maxWidth: "1180px",
        margin: "0 auto",
        padding: "0 24px",
        display: "grid",
        gridTemplateColumns: "1.1fr 0.9fr",
        gap: "60px",
        alignItems: "center"
      }}>
        <div data-reveal="left">
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
            <img src={LOGO_URL} alt="" className="afu-mini-logo" style={{ height: "34px", width: "34px" }} />
            <p style={{
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 600,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              fontSize: "12px",
              color: "#E8A838",
              margin: 0
            }}>About Archer Foundation Uganda</p>
          </div>
          <h2 style={{
            fontSize: "clamp(28px, 4vw, 42px)",
            fontWeight: 700,
            color: COLORS.primary
          }}>Who we are</h2>
          <p style={{ fontSize: "16px", color: COLORS.textLight }}>
            ARCHER Foundation Uganda is a community-focused non-profit organization dedicated to improving the
            health and wellbeing of vulnerable populations across Uganda.
          </p>
          <p style={{ fontSize: "16px", color: COLORS.textLight }}>
            Founded by passionate healthcare professionals, we are committed to reducing health inequalities by
            bringing essential healthcare services directly to communities where access remains limited. We believe
            healthcare is a basic human right for everyone — regardless of income, occupation, gender, age, or
            social background.
          </p>
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "20px",
            margin: "24px 0"
          }}>
            <div style={{ padding: "16px", background: "#FFFFFF", borderRadius: "12px", border: `1px solid ${COLORS.border}` }}>
              <h3 style={{ fontSize: "16px", color: COLORS.primaryLight, marginBottom: "4px" }}>Our Vision</h3>
              <p style={{ fontSize: "14px", color: COLORS.textLight, margin: 0 }}>A healthy and empowered Uganda where every person has access to quality healthcare, dignity, and opportunity.</p>
            </div>
            <div style={{ padding: "16px", background: "#FFFFFF", borderRadius: "12px", border: `1px solid ${COLORS.border}` }}>
              <h3 style={{ fontSize: "16px", color: COLORS.primaryLight, marginBottom: "4px" }}>Our Mission</h3>
              <p style={{ fontSize: "14px", color: COLORS.textLight, margin: 0 }}>Improving wellbeing through accessible healthcare, disease prevention, education, and lasting partnerships.</p>
            </div>
          </div>
          <ul style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            marginTop: "8px"
          }}>
            {[
              "HIV prevention & care",
              "Sexual & reproductive health",
              "Maternal & child health",
              "Mental health",
              "Family planning",
              "Community outreach",
              "Health education",
            ].map((tag) => (
              <li key={tag} style={{
                background: `rgba(10,59,92,0.08)`,
                color: COLORS.primary,
                padding: "6px 14px",
                borderRadius: "999px",
                fontSize: "12.5px",
                fontWeight: 600
              }}>{tag}</li>
            ))}
          </ul>
        </div>

        <div data-reveal="right" style={{ height: "100%" }}>
          <StaticAnimatedImage 
            src={aboutImageUrl}
            alt="Archer Foundation Uganda - Health Awareness and Community Outreach"
          />
        </div>
      </div>
    </section>
  );
}

function WhyWeExist() {
  return (
    <section style={{
      position: "relative",
      color: "#FFFFFF",
      padding: "120px 0"
    }}>
      <div style={{
        position: "absolute",
        inset: 0,
        backgroundImage: `url(${MEDIA.whyExistBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed"
      }} />
      <div style={{
        position: "absolute",
        inset: 0,
        background: `linear-gradient(160deg, ${COLORS.primary}EE, ${COLORS.primaryLight}DD)`
      }} />
      <div style={{
        position: "relative",
        zIndex: 1,
        maxWidth: "1180px",
        margin: "0 auto",
        padding: "0 24px",
        textAlign: "center"
      }}>
        <h2 style={{
          fontSize: "clamp(28px, 4vw, 42px)",
          fontWeight: 700,
          color: "#FFFFFF"
        }} data-reveal="up">
          Healthcare Should Reach Everyone
        </h2>
        <p style={{
          fontSize: "18px",
          maxWidth: "640px",
          color: "rgba(255,255,255,0.88)",
          margin: "0 auto 48px"
        }} data-reveal>
          For many vulnerable individuals, a simple health intervention can mean the difference between suffering
          and receiving hope. Archer Foundation Uganda exists to close the gap.
        </p>

        <div className="stagger-children" style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "20px"
        }}>
          {BARRIERS.map((b) => (
            <div key={b.title} style={{
              background: "rgba(255,255,255,0.07)",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: "18px",
              padding: "30px 20px",
              backdropFilter: "blur(8px)",
              transition: "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1), background 0.3s ease"
            }}>
              <span style={{ fontSize: "34px", display: "block", marginBottom: "12px" }}>{b.icon}</span>
              <h3 style={{ color: "#FFFFFF", fontSize: "18px" }}>{b.title}</h3>
              <p style={{ color: "rgba(255,255,255,0.8)", fontSize: "14px", margin: 0 }}>{b.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProgramCard({ program }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="afu-program-card" style={{
      borderRadius: "20px",
      background: "#FFFFFF",
      border: `1px solid ${COLORS.border}`,
      boxShadow: `0 4px 20px ${COLORS.shadow}`,
      transition: "transform 0.5s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.5s ease, border-color 0.4s ease",
      transformStyle: "preserve-3d"
    }}>
      <div style={{ padding: "30px 24px" }}>
        <span style={{ fontSize: "34px", display: "block", marginBottom: "14px" }}>{program.icon}</span>
        <h3 style={{ fontSize: "18px", fontWeight: 700, color: COLORS.primary }}>{program.title}</h3>
        <p style={{ color: COLORS.textLight, fontSize: "14.5px", lineHeight: 1.6 }}>{program.summary}</p>
        {open && <p style={{
          borderTop: `1px dashed ${COLORS.border}`,
          paddingTop: "14px",
          marginTop: "8px",
          fontSize: "14px",
          color: COLORS.textLight
        }}>{program.more}</p>}
        <button onClick={() => setOpen((o) => !o)} style={{
          background: "none",
          border: "none",
          color: COLORS.primaryLight,
          fontWeight: 600,
          fontFamily: "'Poppins', sans-serif",
          fontSize: "13px",
          padding: 0,
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          marginTop: "10px",
          transition: "color 0.3s ease"
        }}>
          {open ? "Show less" : "Learn more"} <span style={{ transition: "transform 0.3s ease", transform: open ? "rotate(180deg)" : "none" }}>→</span>
        </button>
      </div>
    </div>
  );
}

function Programs() {
  return (
    <section id="programs" style={{
      padding: "100px 0",
      position: "relative",
      background: "#FFFFFF"
    }}>
      <div style={{
        maxWidth: "1180px",
        margin: "0 auto",
        padding: "0 24px"
      }}>
        <div style={{ marginBottom: "48px", textAlign: "center" }} data-reveal="up">
          <p style={{
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 600,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            fontSize: "12px",
            color: "#E8A838",
            marginBottom: "12px"
          }}>Our Programs</p>
          <h2 style={{
            fontSize: "clamp(28px, 4vw, 42px)",
            fontWeight: 700,
            color: COLORS.primary
          }}>How we bring healthcare closer</h2>
          <p style={{ color: COLORS.textLight, maxWidth: "560px", margin: "0 auto" }}>
            Four pillars of impact that drive our mission forward
          </p>
        </div>
        <div className="stagger-children" style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "24px",
          perspective: "1200px"
        }}>
          {PROGRAMS.map((p) => (
            <ProgramCard program={p} key={p.title} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Objectives() {
  const [showAll, setShowAll] = useState(false);
  const objectivesToShow = showAll ? ALL_OBJECTIVES : DISPLAYED_OBJECTIVES;

  return (
    <section id="objectives" style={{
      padding: "100px 0",
      position: "relative",
      background: `linear-gradient(135deg, ${COLORS.primaryBg}, #DCEAF2)`
    }}>
      <div style={{
        maxWidth: "1180px",
        margin: "0 auto",
        padding: "0 24px"
      }}>
        <div style={{ marginBottom: "48px", textAlign: "center" }} data-reveal="up">
          <p style={{
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 600,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            fontSize: "12px",
            color: "#E8A838",
            marginBottom: "12px"
          }}>Our Foundation Objectives</p>
          <h2 style={{
            fontSize: "clamp(28px, 4vw, 42px)",
            fontWeight: 700,
            color: COLORS.primary
          }}>What We Stand For</h2>
          <p style={{ color: COLORS.textLight, maxWidth: "560px", margin: "0 auto" }}>
            The guiding principles that shape our work and impact
          </p>
        </div>

        <div className="stagger-children" style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "20px"
        }}>
          {objectivesToShow.map((objective, index) => {
            const [title, ...descriptionParts] = objective.split(" — ");
            const description = descriptionParts.join(" — ");
            return (
              <div
                key={index}
                style={{
                  background: "#FFFFFF",
                  borderRadius: "16px",
                  padding: "24px 28px",
                  border: `1px solid ${COLORS.border}`,
                  boxShadow: `0 4px 20px ${COLORS.shadow}`,
                  transition: "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.4s ease",
                  display: "flex",
                  gap: "16px",
                  alignItems: "flex-start"
                }}
                className="afu-objective-card"
              >
                <span style={{
                  fontSize: "20px",
                  fontWeight: 700,
                  color: "#E8A838",
                  minWidth: "32px",
                  lineHeight: 1.4,
                  fontFamily: "'Poppins', sans-serif"
                }}>
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3 style={{
                    fontSize: "16px",
                    fontWeight: 700,
                    color: COLORS.primary,
                    marginBottom: "6px"
                  }}>
                    {title}
                  </h3>
                  <p style={{
                    fontSize: "14px",
                    color: COLORS.textLight,
                    margin: 0,
                    lineHeight: 1.6
                  }}>
                    {description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {!showAll && ALL_OBJECTIVES.length > DISPLAYED_OBJECTIVES.length && (
          <div style={{ textAlign: "center", marginTop: "40px" }} data-reveal>
            <button
              onClick={() => setShowAll(true)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "10px",
                padding: "16px 40px",
                borderRadius: "999px",
                fontWeight: 600,
                fontFamily: "'Poppins', sans-serif",
                fontSize: "15px",
                border: `2px solid #E8A838`,
                background: "transparent",
                color: COLORS.primary,
                transition: "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), background 0.3s ease, box-shadow 0.3s ease"
              }}
              className="afu-objectives-btn"
            >
              See More Objectives ({ALL_OBJECTIVES.length - DISPLAYED_OBJECTIVES.length} remaining)
              <span style={{ fontSize: "18px" }}>↓</span>
            </button>
          </div>
        )}

        {showAll && (
          <div style={{ textAlign: "center", marginTop: "40px" }} data-reveal>
            <button
              onClick={() => setShowAll(false)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "10px",
                padding: "16px 40px",
                borderRadius: "999px",
                fontWeight: 600,
                fontFamily: "'Poppins', sans-serif",
                fontSize: "15px",
                border: `2px solid ${COLORS.border}`,
                background: "transparent",
                color: COLORS.textLight,
                transition: "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), background 0.3s ease"
              }}
            >
              Show Less
              <span style={{ fontSize: "18px" }}>↑</span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

function WhySupport() {
  return (
    <section id="support" style={{
      position: "relative",
      color: "#FFFFFF",
      padding: "120px 0",
      textAlign: "center"
    }}>
      <div style={{
        position: "absolute",
        inset: 0,
        backgroundImage: `url(${MEDIA.supportBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed"
      }} />
      <div style={{
        position: "absolute",
        inset: 0,
        background: `linear-gradient(135deg, ${COLORS.primary}E6, rgba(232,168,56,0.65))`
      }} />
      <div style={{
        position: "relative",
        zIndex: 1,
        maxWidth: "1180px",
        margin: "0 auto",
        padding: "0 24px"
      }}>
        <p style={{
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 600,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          fontSize: "12px",
          color: "#f3c169",
          marginBottom: "12px"
        }} data-reveal>
          Why Support Archer Foundation Uganda?
        </p>
        <h2 style={{
          fontSize: "clamp(28px, 4vw, 42px)",
          fontWeight: 700,
          color: "#FFFFFF"
        }} data-reveal="up">
          Your Contribution Can Save a Life
        </h2>
        <div className="stagger-children" style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "16px",
          maxWidth: "720px",
          margin: "40px auto 44px"
        }}>
          {SUPPORT_ITEMS.map((item) => (
            <div key={item.label} style={{
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: "14px",
              padding: "18px 12px",
              fontSize: "14px",
              fontWeight: 600,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
              transition: "transform 0.3s ease, background 0.3s ease"
            }}>
              <span style={{ fontSize: "24px" }}>{item.icon}</span> {item.label}
            </div>
          ))}
        </div>
        <a href="#donate" style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          padding: "18px 40px",
          borderRadius: "999px",
          fontWeight: 600,
          fontFamily: "'Poppins', sans-serif",
          fontSize: "16px",
          border: "2px solid transparent",
          transition: "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), background 0.3s ease, box-shadow 0.3s ease",
          whiteSpace: "nowrap",
          background: "linear-gradient(135deg, #E8A838, #d49a2e)",
          color: "#0A2A3F",
          boxShadow: "0 8px 30px rgba(232,168,56,0.4)"
        }} data-reveal="scale">
          Donate Now
        </a>
      </div>
    </section>
  );
}

function Donate() {
  return (
    <section id="donate" style={{
      padding: "100px 0",
      position: "relative",
      background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
      color: "#FFFFFF",
      textAlign: "center"
    }}>
      <div style={{
        maxWidth: "1180px",
        margin: "0 auto",
        padding: "0 24px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center"
      }} data-reveal="up">
        <h2 style={{
          fontSize: "clamp(28px, 4vw, 42px)",
          fontWeight: 700,
          color: "#FFFFFF"
        }}>Be Part of the Change</h2>
        <p style={{
          fontSize: "18px",
          maxWidth: "640px",
          color: "rgba(255,255,255,0.85)",
          margin: "0 auto 32px"
        }}>
          Whether you are an individual donor, organisation, healthcare provider, or development partner, your
          support creates measurable impact. Together, we can save lives.
        </p>
        <div style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "16px",
          justifyContent: "center"
        }}>
          <a href="#contact" style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "18px 38px",
            borderRadius: "999px",
            fontWeight: 600,
            fontFamily: "'Poppins', sans-serif",
            fontSize: "16px",
            border: "2px solid transparent",
            transition: "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), background 0.3s ease, box-shadow 0.3s ease",
            whiteSpace: "nowrap",
            background: "linear-gradient(135deg, #E8A838, #d49a2e)",
            color: "#0A2A3F",
            boxShadow: "0 8px 30px rgba(232,168,56,0.35)",
          }}>
            Donate Today
          </a>
          <a href="#contact" style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "18px 38px",
            borderRadius: "999px",
            fontWeight: 600,
            fontFamily: "'Poppins', sans-serif",
            fontSize: "16px",
            border: "2px solid rgba(255,255,255,0.4)",
            transition: "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), background 0.3s ease",
            whiteSpace: "nowrap",
            background: "transparent",
            color: "#FFFFFF"
          }}>
            Partner with Us
          </a>
          <a href="#contact" style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "18px 38px",
            borderRadius: "999px",
            fontWeight: 600,
            fontFamily: "'Poppins', sans-serif",
            fontSize: "16px",
            border: "2px solid rgba(255,255,255,0.4)",
            transition: "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), background 0.3s ease",
            whiteSpace: "nowrap",
            background: "transparent",
            color: "#FFFFFF"
          }}>
            Volunteer
          </a>
        </div>
      </div>
    </section>
  );
}

function Partners() {
  return (
    <section id="partners" style={{
      padding: "100px 0",
      position: "relative",
      background: "#FFFFFF",
      textAlign: "center"
    }}>
      <div style={{
        maxWidth: "1180px",
        margin: "0 auto",
        padding: "0 24px"
      }}>
        <div style={{ marginBottom: "48px" }} data-reveal="up">
          <p style={{
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 600,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            fontSize: "12px",
            color: "#E8A838",
            marginBottom: "12px"
          }}>Partnerships</p>
          <h2 style={{
            fontSize: "clamp(28px, 4vw, 42px)",
            fontWeight: 700,
            color: COLORS.primary
          }}>Working Together for Greater Impact</h2>
        </div>
        <div className="stagger-children" style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "20px"
        }}>
          {PARTNERS.map((name) => (
            <div key={name} className="afu-partner-logo" style={{
              background: `linear-gradient(135deg, ${COLORS.primaryBg}, #FFFFFF)`,
              borderRadius: "16px",
              padding: "32px 14px",
              fontWeight: 700,
              fontFamily: "'Poppins', sans-serif",
              color: "#94A3B8",
              filter: "grayscale(0.8)",
              transition: "filter 0.4s ease, color 0.4s ease, transform 0.4s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.4s ease",
              fontSize: "14.5px",
              border: `1px solid ${COLORS.border}`
            }}>
              {name}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const [status, setStatus] = useState("idle");

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    setStatus("sent");
  }, []);

  return (
    <section id="contact" style={{
      padding: "100px 0",
      position: "relative",
      background: `linear-gradient(135deg, ${COLORS.primaryBg}, #DCEAF2)`
    }}>
      <div style={{
        maxWidth: "1180px",
        margin: "0 auto",
        padding: "0 24px",
        display: "grid",
        gridTemplateColumns: "1.1fr 0.9fr",
        gap: "50px"
      }}>
        <div data-reveal="left">
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
            <img src={LOGO_URL} alt="" className="afu-mini-logo" style={{ height: "34px", width: "34px" }} />
            <p style={{
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 600,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              fontSize: "12px",
              color: "#E8A838",
              margin: 0
            }}>Join Our Mission</p>
          </div>
          <h2 style={{
            fontSize: "clamp(28px, 4vw, 42px)",
            fontWeight: 700,
            color: COLORS.primary
          }}>Let's talk</h2>
          <p style={{
            fontSize: "17px",
            maxWidth: "600px",
            color: COLORS.textLight
          }}>
            Would you like to support our work, partner with us, volunteer, or learn more about our programs? We'd
            love to hear from you.
          </p>

          {status === "sent" ? (
            <div style={{
              background: `rgba(10,59,92,0.08)`,
              border: `1px solid ${COLORS.border}`,
              color: COLORS.primary,
              padding: "20px",
              borderRadius: "14px",
              marginTop: "24px",
              fontWeight: 500,
              fontSize: "16px"
            }}>
              Thank you — your message has been received. We'll be in touch soon.
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{
              display: "flex",
              flexDirection: "column",
              gap: "18px",
              marginTop: "24px"
            }}>
              <label style={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
                fontSize: "13px",
                fontWeight: 600,
                color: COLORS.primary
              }}>
                Name
                <input type="text" name="name" required placeholder="Your full name" className="afu-contact-input" style={{
                  fontFamily: "inherit",
                  fontSize: "15px",
                  padding: "14px 16px",
                  borderRadius: "12px",
                  border: "2px solid #E2E8F0",
                  background: "#FFFFFF",
                  transition: "border-color 0.3s ease, box-shadow 0.3s ease",
                  outline: "none"
                }} />
              </label>
              <label style={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
                fontSize: "13px",
                fontWeight: 600,
                color: COLORS.primary
              }}>
                Email
                <input type="email" name="email" required placeholder="you@example.com" className="afu-contact-input" style={{
                  fontFamily: "inherit",
                  fontSize: "15px",
                  padding: "14px 16px",
                  borderRadius: "12px",
                  border: "2px solid #E2E8F0",
                  background: "#FFFFFF",
                  transition: "border-color 0.3s ease, box-shadow 0.3s ease",
                  outline: "none"
                }} />
              </label>
              <label style={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
                fontSize: "13px",
                fontWeight: 600,
                color: COLORS.primary
              }}>
                Message
                <textarea name="message" rows={5} required placeholder="Tell us how you'd like to get involved" className="afu-contact-input" style={{
                  fontFamily: "inherit",
                  fontSize: "15px",
                  padding: "14px 16px",
                  borderRadius: "12px",
                  border: "2px solid #E2E8F0",
                  background: "#FFFFFF",
                  resize: "vertical",
                  transition: "border-color 0.3s ease, box-shadow 0.3s ease",
                  outline: "none"
                }} />
              </label>
              <button type="submit" className="afu-btn-gold" style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "16px 32px",
                borderRadius: "999px",
                fontWeight: 600,
                fontFamily: "'Poppins', sans-serif",
                fontSize: "15px",
                border: "2px solid transparent",
                transition: "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), background 0.3s ease, box-shadow 0.3s ease",
                whiteSpace: "nowrap",
                background: "linear-gradient(135deg, #E8A838, #d49a2e)",
                color: "#0A2A3F",
                boxShadow: "0 8px 24px rgba(232,168,56,0.3)",
                alignSelf: "flex-start"
              }}>
                Send Message
              </button>
            </form>
          )}
        </div>

        <div style={{
          display: "flex",
          flexDirection: "column",
          gap: "14px"
        }} data-reveal="right">
          <div style={{
            background: "#FFFFFF",
            borderRadius: "16px",
            padding: "20px 24px",
            border: `1px solid ${COLORS.border}`,
            boxShadow: `0 4px 16px ${COLORS.shadow}`
          }}>
            <h3 style={{
              fontSize: "12px",
              color: "#E8A838",
              marginBottom: "4px",
              textTransform: "uppercase",
              letterSpacing: "0.08em"
            }}>Email</h3>
            <a href="mailto:archerfoundationug@gmail.com" style={{
              fontSize: "16px",
              fontWeight: 600,
              color: COLORS.primary,
              margin: 0
            }}>archerfoundationug@gmail.com</a>
          </div>
          <div style={{
            background: "#FFFFFF",
            borderRadius: "16px",
            padding: "20px 24px",
            border: `1px solid ${COLORS.border}`,
            boxShadow: `0 4px 16px ${COLORS.shadow}`
          }}>
            <h3 style={{
              fontSize: "12px",
              color: "#E8A838",
              marginBottom: "4px",
              textTransform: "uppercase",
              letterSpacing: "0.08em"
            }}>Phone</h3>
            <a href="tel:+256754090012" style={{
              fontSize: "16px",
              fontWeight: 600,
              color: COLORS.primary,
              margin: 0
            }}>+256 754 090012</a>
          </div>
          <div style={{
            background: "#FFFFFF",
            borderRadius: "16px",
            padding: "20px 24px",
            border: `1px solid ${COLORS.border}`,
            boxShadow: `0 4px 16px ${COLORS.shadow}`
          }}>
            <h3 style={{
              fontSize: "12px",
              color: "#E8A838",
              marginBottom: "4px",
              textTransform: "uppercase",
              letterSpacing: "0.08em"
            }}>Location</h3>
            <p style={{
              fontSize: "16px",
              fontWeight: 600,
              color: COLORS.primary,
              margin: 0
            }}>Kampala, Uganda</p>
          </div>
          <div style={{
            borderRadius: "16px",
            overflow: "hidden",
            border: `1px solid ${COLORS.border}`,
            flex: 1,
            minHeight: "220px",
            boxShadow: `0 4px 16px ${COLORS.shadow}`
          }}>
            <iframe
              title="Archer Foundation Uganda location — Kampala"
              src="https://www.google.com/maps?q=Kampala,Uganda&output=embed"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              style={{
                width: "100%",
                height: "100%",
                minHeight: "220px",
                border: 0
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  return (
    <footer style={{
      background: COLORS.primary,
      color: "rgba(255,255,255,0.75)",
      padding: "40px 0",
      borderTop: "1px solid rgba(255,255,255,0.05)"
    }}>
      <div style={{
        maxWidth: "1180px",
        margin: "0 auto",
        padding: "0 24px",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "16px"
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          fontFamily: "'Poppins', sans-serif",
          color: "#FFFFFF",
          fontWeight: 600
        }}>
          <img 
            src={LOGO_URL} 
            alt="Archer Foundation Uganda Logo" 
            className="afu-mini-logo"
            style={{ 
              height: "32px", 
              width: "32px", 
            }}
          />
          <span style={{ fontSize: "14px" }}>Archer Foundation Uganda</span>
        </div>
        <p style={{
          fontSize: "13px",
          margin: 0,
          opacity: 0.7
        }}>© {new Date().getFullYear()} Archer Foundation Uganda. All rights reserved.</p>
        <div className="afu-footer-socials" style={{
          display: "flex",
          gap: "10px"
        }}>
          <a href="#top" aria-label="Facebook" style={{
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            border: "1px solid rgba(255,255,255,0.15)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "14px",
            transition: "background 0.3s ease, border-color 0.3s ease"
          }}>f</a>
          <a href="#top" aria-label="Twitter / X" style={{
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            border: "1px solid rgba(255,255,255,0.15)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "14px",
            transition: "background 0.3s ease, border-color 0.3s ease"
          }}>𝕏</a>
          <a href="#top" aria-label="Instagram" style={{
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            border: "1px solid rgba(255,255,255,0.15)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "14px",
            transition: "background 0.3s ease, border-color 0.3s ease"
          }}>◎</a>
          <a href="#top" aria-label="LinkedIn" style={{
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            border: "1px solid rgba(255,255,255,0.15)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "14px",
            transition: "background 0.3s ease, border-color 0.3s ease"
          }}>in</a>
        </div>
        <button onClick={scrollTop} aria-label="Back to top" style={{
          width: "40px",
          height: "40px",
          borderRadius: "50%",
          background: "linear-gradient(135deg, #E8A838, #d49a2e)",
          color: "#0A2A3F",
          border: "none",
          fontSize: "18px",
          fontWeight: 800,
          transition: "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.3s ease"
        }}>
          ↑
        </button>
      </div>
    </footer>
  );
}

function VideoModal({ open, onClose }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div style={{
      position: "fixed",
      inset: 0,
      background: "rgba(10,18,32,0.9)",
      zIndex: 10001,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "24px",
      animation: "afuFadeIn 0.3s cubic-bezier(0.22, 1, 0.36, 1)"
    }} onClick={onClose}>
      <div style={{
        width: "min(920px, 100%)",
        position: "relative"
      }} onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} aria-label="Close video" style={{
          position: "absolute",
          top: "-48px",
          right: 0,
          background: "none",
          border: "none",
          color: "#FFFFFF",
          fontSize: "24px",
          width: "40px",
          height: "40px",
          opacity: 0.7,
          transition: "opacity 0.3s ease"
        }}>
          ✕
        </button>
        <div style={{
          position: "relative",
          paddingTop: "56.25%",
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 40px 80px rgba(0,0,0,0.6)"
        }}>
          <iframe
            src={MEDIA.storyVideo}
            title="Archer Foundation Uganda — Our Story"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              border: 0
            }}
          />
        </div>
      </div>
    </div>
  );
}

// ===== Root Component =====

export default function ArcherHomepage() {
  const { scrolled, progress, activeSection } = useScrollProgressAndNav();
  const [menuOpen, setMenuOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [introDone, setIntroDone] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  useReveal();

  // Preload gallery images in background when page loads
  useEffect(() => {
    if (introDone) {
      const timer = setTimeout(() => {
        preloadGalleryImages(GALLERY_IMAGES);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [introDone]);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .afu-nav-links a:hover {
        color: #E8A838 !important;
        opacity: 1 !important;
      }
      
      .afu-nav-links a:focus-visible {
        opacity: 1 !important;
      }

      .afu-gallery-trigger:hover {
        opacity: 1 !important;
        color: #E8A838 !important;
        border-bottom-color: #E8A838 !important;
      }

      .afu-gallery-item:hover {
        background: rgba(10,59,92,0.06) !important;
      }
      
      .afu-program-card:hover {
        transform: translateY(-10px) !important;
        box-shadow: 0 24px 60px rgba(10,59,92,0.12) !important;
        border-color: #E8A838 !important;
      }

      .afu-objective-card:hover {
        transform: translateY(-4px) !important;
        box-shadow: 0 12px 40px rgba(10,59,92,0.10) !important;
        border-color: #E8A838 !important;
      }

      .afu-objectives-btn:hover {
        transform: translateY(-4px) !important;
        background: rgba(232,168,56,0.08) !important;
        box-shadow: 0 12px 36px rgba(232,168,56,0.15) !important;
      }
      
      .afu-btn-gold:hover {
        transform: translateY(-4px) !important;
        background: linear-gradient(135deg, #f3c169, #d49a2e) !important;
        box-shadow: 0 12px 36px rgba(232,168,56,0.5) !important;
      }

      /* Top-nav Donate button: yellow, deliberately NO hover changes */
      .afu-nav-donate-btn,
      .afu-nav-donate-btn:hover,
      .afu-nav-donate-btn:focus,
      .afu-nav-donate-btn:active {
        background: linear-gradient(135deg, #E8A838, #d49a2e) !important;
        color: #0A2A3F !important;
        transform: none !important;
        box-shadow: 0 8px 24px rgba(232,168,56,0.35) !important;
        filter: none !important;
      }
      
      .afu-contact-input:focus {
        border-color: #E8A838 !important;
        box-shadow: 0 0 0 4px rgba(232,168,56,0.12) !important;
      }
      
      .afu-footer-socials a:hover {
        background: rgba(232,168,56,0.2) !important;
        border-color: #E8A838 !important;
        color: #E8A838 !important;
      }
      
      .afu-partner-logo:hover {
        filter: grayscale(0) !important;
        color: ${COLORS.primary} !important;
        transform: translateY(-6px) !important;
        box-shadow: 0 8px 24px rgba(10,59,92,0.08) !important;
      }

      /* Mobile Styles — modern dropdown menu */
      @media (max-width: 900px) {
        .afu-navbar > div {
          position: relative !important;
        }
        .afu-nav-links {
          position: absolute !important;
          top: calc(100% + 10px) !important;
          left: 16px !important;
          right: 16px !important;
          flex-direction: column !important;
          align-items: stretch !important;
          gap: 4px !important;
          background: rgba(255,255,255,0.98) !important;
          -webkit-backdrop-filter: blur(20px) !important;
          backdrop-filter: blur(20px) !important;
          border-radius: 20px !important;
          padding: 10px !important;
          box-shadow: 0 24px 60px rgba(10,30,50,0.35) !important;
          border: 1px solid rgba(10,59,92,0.08) !important;
          max-height: 0 !important;
          opacity: 0 !important;
          overflow: hidden !important;
          pointer-events: none !important;
          transform: translateY(-10px) !important;
          transition: max-height 0.45s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.3s ease, transform 0.35s cubic-bezier(0.22, 1, 0.36, 1) !important;
          z-index: 1000 !important;
        }
        .afu-nav-links.is-open {
          max-height: 640px !important;
          opacity: 1 !important;
          pointer-events: auto !important;
          transform: translateY(0) !important;
        }
        .afu-nav-links a {
          color: ${COLORS.primary} !important;
          font-size: 16px !important;
          font-weight: 600 !important;
          padding: 14px 16px !important;
          border-radius: 12px !important;
          border-bottom: none !important;
          opacity: 1 !important;
          width: 100% !important;
          display: flex !important;
          align-items: center !important;
          justify-content: flex-start !important;
          gap: 12px !important;
          transition: background 0.25s ease, color 0.25s ease !important;
        }
        .afu-nav-links a::after {
          content: '→';
          font-size: 14px;
          opacity: 0.35;
          margin-left: auto;
          transition: transform 0.25s ease, opacity 0.25s ease;
        }
        .afu-nav-links a:hover,
        .afu-nav-links a:active {
          background: rgba(10,59,92,0.06) !important;
          color: ${COLORS.primaryLight} !important;
        }
        .afu-nav-links a:hover::after {
          opacity: 0.8;
          transform: translateX(3px);
        }
        
        /* Mobile Gallery button - align icon and text properly */
        .afu-nav-links .afu-gallery-trigger {
          color: ${COLORS.primary} !important;
          font-size: 16px !important;
          font-weight: 600 !important;
          padding: 14px 16px !important;
          border-radius: 12px !important;
          border-bottom: none !important;
          opacity: 1 !important;
          width: 100% !important;
          display: flex !important;
          align-items: center !important;
          justify-content: flex-start !important;
          gap: 12px !important;
        }
        .afu-nav-links .afu-gallery-trigger::after {
          content: '→';
          font-size: 14px;
          opacity: 0.35;
          margin-left: auto;
          transition: transform 0.25s ease, opacity 0.25s ease;
        }
        .afu-nav-links .afu-gallery-trigger:hover::after {
          opacity: 0.8;
          transform: translateX(3px);
        }
        
        .afu-mobile-cta {
          display: flex !important;
          margin-top: 8px !important;
        }
        .afu-mobile-cta::after {
          content: '' !important;
        }
        .afu-burger {
          display: flex !important;
        }
        .afu-burger:hover {
          background: rgba(255,255,255,0.16) !important;
        }
        .afu-burger.is-open {
          background: rgba(232,168,56,0.18) !important;
          border-color: rgba(232,168,56,0.5) !important;
        }
        .afu-burger.is-open span:nth-child(1) {
          transform: translateY(7px) rotate(45deg) !important;
        }
        .afu-burger.is-open span:nth-child(2) {
          opacity: 0 !important;
          transform: scale(0) !important;
        }
        .afu-burger.is-open span:nth-child(3) {
          transform: translateY(-7px) rotate(-45deg) !important;
        }

        /* Hide desktop donate button on mobile */
        .afu-nav-donate-btn {
          display: none !important;
        }

        /* Mobile gallery improvements */
        .afu-gallery-grid {
          grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)) !important;
          gap: 10px !important;
          padding: 12px !important;
        }

        .afu-brand-text strong { display: inline; }
        
        /* Gallery categories - horizontal scroll on mobile */
        .afu-gallery-categories {
          flex-wrap: nowrap !important;
          overflow-x: auto !important;
          overflow-y: hidden !important;
          -webkit-overflow-scrolling: touch !important;
          scrollbar-width: none !important;
          -ms-overflow-style: none !important;
          padding: 2px 0 8px !important;
          gap: 6px !important;
          justify-content: flex-start !important;
        }
        .afu-gallery-categories::-webkit-scrollbar {
          display: none !important;
        }
        .afu-gallery-categories button {
          font-size: 11px !important;
          padding: 5px 12px !important;
          flex-shrink: 0 !important;
        }
        
        /* Gallery header adjustments for mobile */
        .afu-gallery-header {
          padding: 10px 52px 10px 12px !important;
          gap: 6px !important;
        }
        .afu-gallery-header > div:first-child {
          font-size: 12px !important;
        }
        .afu-gallery-header > div:first-child span {
          font-size: 11px !important;
        }
      }

      @media (max-width: 480px) {
        .afu-nav-links {
          left: 10px !important;
          right: 10px !important;
          top: calc(100% + 8px) !important;
        }
        .afu-gallery-grid {
          grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)) !important;
          gap: 8px !important;
          padding: 8px !important;
        }
        .afu-brand-text {
          font-size: 13px !important;
        }
        .afu-gallery-categories button {
          font-size: 10px !important;
          padding: 4px 10px !important;
        }
        .afu-gallery-header {
          padding: 8px 44px 8px 10px !important;
        }
      }

      @media (max-width: 768px) {
        .stagger-children {
          grid-template-columns: repeat(2, 1fr) !important;
        }
        
        [data-reveal] {
          opacity: 1 !important;
          transform: none !important;
        }
        
        section {
          padding: 60px 0 !important;
        }

        #about > div {
          grid-template-columns: 1fr !important;
        }
        .about-image-static {
          min-height: 300px !important;
          order: -1 !important;
        }

        #why-exist .stagger-children {
          grid-template-columns: repeat(2, 1fr) !important;
        }

        #programs .stagger-children {
          grid-template-columns: repeat(2, 1fr) !important;
        }

        #objectives .stagger-children {
          grid-template-columns: 1fr !important;
        }

        #support .stagger-children {
          grid-template-columns: repeat(2, 1fr) !important;
        }

        #partners .stagger-children {
          grid-template-columns: repeat(2, 1fr) !important;
        }

        #contact > div {
          grid-template-columns: 1fr !important;
        }

        .afu-hero-text {
          font-size: 32px !important;
        }
      }

      @media (max-width: 560px) {
        .stagger-children {
          grid-template-columns: 1fr !important;
        }

        #why-exist .stagger-children {
          grid-template-columns: 1fr !important;
        }

        #programs .stagger-children {
          grid-template-columns: 1fr !important;
        }

        #objectives .stagger-children {
          grid-template-columns: 1fr !important;
        }

        #support .stagger-children {
          grid-template-columns: 1fr !important;
        }

        #partners .stagger-children {
          grid-template-columns: 1fr !important;
        }

        #impact .stagger-children {
          grid-template-columns: 1fr 1fr !important;
        }

        .afu-about-columns {
          grid-template-columns: 1fr !important;
        }
      }

      /* Chrome mobile theme color - light blue */
      @supports (-webkit-tap-highlight-color: transparent) {
        body {
          background: ${COLORS.primaryBg} !important;
        }
        html {
          background: ${COLORS.primaryBg} !important;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .about-static-image {
          animation: none !important;
        }
        .afu-word {
          opacity: 1 !important;
          transform: none !important;
          animation: none !important;
        }
        [data-reveal], [data-reveal="left"], [data-reveal="right"], 
        [data-reveal="up"], [data-reveal="scale"], .stagger-children > * {
          opacity: 1 !important;
          transform: none !important;
          transition: none !important;
        }
        .afu-archer-handwriting::before {
          animation: none !important;
          clip-path: inset(0 0% 0 0) !important;
          opacity: 1 !important;
          color: rgba(232,168,56,0.15) !important;
        }
        .afu-nav-links a {
          opacity: 1 !important;
          transform: none !important;
        }
      }

      /* Gallery carousel image sizing fixes */
      .afu-gallery-carousel-img {
        max-width: 100%;
        max-height: 70vh;
        min-height: 300px;
        object-fit: contain;
        border-radius: 12px;
        box-shadow: 0 20px 60px rgba(0,0,0,0.4);
      }
      
      /* Desktop: make carousel images larger */
      @media (min-width: 901px) {
        .afu-gallery-carousel-img {
          max-height: 75vh;
          min-height: 400px;
        }
        .afu-gallery-grid {
          grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)) !important;
        }
      }
    `;
    document.head.appendChild(style);

    const meta = document.createElement('meta');
    meta.name = 'theme-color';
    meta.content = COLORS.primaryBg;
    document.head.appendChild(meta);

    return () => {
      document.head.removeChild(style);
      document.head.removeChild(meta);
    };
  }, []);

  return (
    <div className="afu-root">
      <GlobalStyles />

      {!introDone && <IntroOverlay onFinish={() => setIntroDone(true)} />}

      {introDone && (
        <div style={{ animation: "afuSiteFadeIn 0.6s ease" }}>
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            height: "3px",
            background: "linear-gradient(90deg, #E8A838, #f3c169, #E8A838)",
            backgroundSize: "200% 100%",
            zIndex: 10000,
            transition: "width 0.15s ease-out",
            width: `${progress}%`,
            animation: "afuShimmer 3s linear infinite"
          }} />

          <Navbar
            scrolled={scrolled}
            menuOpen={menuOpen}
            setMenuOpen={setMenuOpen}
            onOpenGallery={() => setGalleryOpen(true)}
            activeSection={activeSection}
          />

          <main>
            <Hero onPlayVideo={() => setVideoOpen(true)} />
            <ImpactSnapshot />
            <About />
            <WhyWeExist />
            <Programs />
            <Objectives />
            <WhySupport />
            <Donate />
            <Partners />
            <Contact />
          </main>

          <Footer />
          <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} />
          <EnhancedGalleryModal open={galleryOpen} onClose={() => setGalleryOpen(false)} />
        </div>
      )}
    </div>
  );
}